<template>
  <div
    ref="select"
    :class="['plv-ia-select', `plv-ia-select--${theme}`, {
      'plv-ia-select--focus pws-vclass-select--focus': isFocus,
    }]"
  >
    <div class="plv-ia-select__result" @click="isFocus = !isFocus">
      <span class="plv-ia-select__result__txt">{{ optionsData[modelValue] }}</span>
      <i class="plv-ia-select__arrow"></i>
    </div>

    <div
      class="plv-ia-select__options"
      @click="handleClick"
    >
      <div
        v-for="(item, index) in options"
        :key="`option${index}`"
        :class="['plv-ia-select__options-item', {
          'plv-ia-select__options-item--active pws-vclass-select__options-item--active': item.value === modelValue
        }]"
        :data-value="item.value"
      >
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<script>
import { isParent } from '@/assets/dom';

export default {
  name: 'CommonSelect',
  model: {
    prop: 'modelValue',
    event: 'change'
  },

  props: {
    modelValue: undefined,

    optionsData: {
      type: Object,
      default: null,
    },

    theme: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      value: '',
      isFocus: false,
    };
  },

  computed: {
    options() {
      const list = [];
      for (const key in this.optionsData) {
        const label = this.optionsData[key];
        list.push({
          value: key,
          label,
        });
      }
      return list;
    }
  },

  watch: {
    modelValue(newVal) {
      this.value = newVal;
    }
  },

  mounted() {
    document.addEventListener('click', this.hideOptions);
  },

  beforeDestory() {
    document.removeEventListener('click', this.hideOptions);
  },

  methods: {
    handleClick(e) {
      const value = e.target.getAttribute('data-value');
      if (typeof value === 'undefined' || value === null) return;
      this.isFocus = false;

      this.$emit('change', value);
    },

    hideOptions(e) {
      if (!this.$refs.select) return;
      if (!isParent(e.target, this.$refs.select)) {
        this.isFocus = false;
      }
    }
  }
};
</script>

<style lang="scss">
.plv-ia-select {
  position: relative;
  width: 400px;
}
.plv-ia-select--focus {
  .plv-ia-select__result {
    border-color: #2196F3;
  }

  .plv-ia-select__arrow:before {
    transform: rotate(-45deg);
  }

  .plv-ia-select__options {
    display: block;
  }
}
.plv-ia-select__result {
  padding: 0 8px;
  width: 100%;
  box-sizing: border-box;
  line-height: 38px;
  border: 1px solid #CFD8DC;
  color: #333;
  border-radius: 2px;
  position: relative;
  user-select: none;
  cursor: pointer;
}
.plv-ia-select__arrow {
  position: absolute;
  top: 0;
  right: 0;
  width: 38px;
  height: 100%;
  cursor: pointer;
  line-height: 38px;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    margin: auto;
    width: 5px;
    height: 5px;
    border: 1px solid transparent;
    border-top: 1px solid #adadc0;
    border-right: 1px solid #adadc0;
    transform: rotate(135deg);
    transition: transform 0.5s;
  }
}
.plv-ia-select__options {
  display: none;
  position: absolute;
  width: 100%;
  z-index: 1;
  background: #fff;
  box-sizing: border-box;
  border: 1px solid #CFD8DC;
  color: #333;
  border-radius: 2px;
  line-height: 40px;
  transition: height 0.5s;
}
.plv-ia-select__options-item {
  padding: 0 8px;
  box-sizing: border-box;
  cursor: pointer;
  &:hover {
    background: #E3F2FD;
  }
}
.plv-ia-select__options-item--active {
  color: #2196F3;
}

@mixin smallSize($bgColor1, $bgColor2, $bgColor3, $fColor1, $fColor2, $fColor3) {
  &.plv-ia-select--focus {
    .plv-ia-select__result {
      background: $bgColor1;
      border-color: $bgColor1;
      color: $fColor1;
    }

    .plv-ia-select__arrow {
      transform: rotate(-180deg);
    }
  }

  .plv-ia-select__result {
    width: 100%;
    line-height: 22px;
    font-size: 12px;
    background: $bgColor2;
    border: 1px solid $bgColor2;
    color: $fColor2;
    border-radius: 0;

    &:hover {
      background: $bgColor1;
      border-color: $bgColor1;
      color: $fColor1;
    }
  }

  .plv-ia-select__result__txt {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }

  .plv-ia-select__arrow {
    width: 24px;
    height: 24px;
    top: -1px;
    cursor: pointer;
    line-height: 24px;
    background: url(./images/icon-arrow.png) no-repeat;
    background-size: contain;
    transition: transform 0.5s;

    &:before {
      width: 0;
      height: 0;
      border: 0;
    }
  }

  .plv-ia-select__options {
    border: 0;
    max-height: 200px;
    overflow: auto;
  }

  .plv-ia-select__options-item {
    line-height: 32px;
    font-size: 12px;
    background: $bgColor1;
    color: $fColor1;

    &:hover {
      background: $bgColor3;
      color: $fColor1;
    }

    &.plv-ia-select__options-item--active {
      color: $fColor3;
    }
  }
}

.plv-ia-select--black {
  @include smallSize(#3E3F4E, #1A1B1F, #2B2C35, #FFFFFF, #ADADC0, #3082FE);
}

.plv-ia-select--white {
  @include smallSize(#F6F8FF, #F6F8FF, #EEEFF6, #333333, #333333, #4991E2);
  .plv-ia-select__result {
    &:hover {
      border-color: #D9DCE6;
    }
  }
  &.plv-ia-select--focus {
    .plv-ia-select__result {
      border-color: #D9DCE6;
      &:after {
        content: '';
        width: 100%;
        height: 2px;
        background: #F6F8FF;
        padding: 0 1px;
        box-sizing: border-box;
        position: absolute;
        bottom: -2px;
        left: 0;
        z-index: 2;
      }
    }
    .plv-ia-select__options {
      border: 1px solid #D9DCE6;
      border-radius: 0;
    }
  }
}

.plv-ia-select--black2 {
  @include smallSize(#2b334c, #2b334c, #303850, rgba(#fff, .6), #ADADC0, #3082FE);
}
</style>
